import IframeResizer from '@rio-cloud/iframe-resizer';
import { RioNotifications } from '@rio-cloud/rio-notifications-component';
import ApplicationHeader from '@rio-cloud/rio-uikit/ApplicationHeader';
import type { ModulePropType } from '@rio-cloud/rio-uikit/components/applicationHeader/ApplicationHeader';
import { DefaultUserMenu } from '@rio-cloud/rio-user-menu-component';
import { lazy, Suspense } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import { config } from '@/config';
import { FeatureToggles } from '@/configuration/featureToggle/featureToggles';
import FeedbackButton from '@/features/header/FeedbackButton';
import { UserManualButton } from '@/features/userManual/components/UserManualButton';
import { useFeatureToggle } from '@/hooks/useFeatureToggle';
import { useLocaleStore } from '@/stores/localeStore';
import { useUserStore } from '@/stores/userStore';

const DevUserMenu = lazy(() => import('@/components/DevUserMenu'));

export interface AppHeaderProps {
    navItems: ModulePropType[];
}

const AppHeader = ({ navItems }: AppHeaderProps) => {
    const environment = import.meta.env.PROD ? 'production' : 'local';
    const { account } = useUserStore();
    const { displayLocale } = useLocaleStore();
    const { value: isFeedbackBannerEnabled } = useFeatureToggle(FeatureToggles.ENABLE_FEEDBACK_BANNER);

    const renderUserMenu = () => {
        if (config.serviceEnvironment === 'production') {
            return <DefaultUserMenu key='user-menu' environment={environment} locale={displayLocale} />;
        }

        return (
            // biome-ignore lint/complexity/noUselessFragments: it is necessary
            <Suspense fallback={<></>}>
                <DevUserMenu key='dev-user-menu' />
            </Suspense>
        );
    };

    const renderAccountItems = () =>
        account?.isOnboarded
            ? [
                  ...(isFeedbackBannerEnabled ? [] : [<FeedbackButton key='feedback' />]),
                  <UserManualButton key='user-manual' />,
                  <RioNotifications key='rio-notifications' />,
              ]
            : [];

    const actionBarItems = [...renderAccountItems(), renderUserMenu()].filter(Boolean);

    return (
        <ApplicationHeader
            label={<FormattedMessage id='userManual.modalTitle' />}
            appNavigator={
                environment === 'production' ? (
                    <IframeResizer className='iFrameResizer' src={config.backend.MENU_SERVICE} />
                ) : null
            }
            homeRoute={<Link to={config.homeRoute || ''} />}
            navItems={navItems}
            actionBarItems={actionBarItems}
        />
    );
};

export default AppHeader;
